/**
 * Single source of truth for the `user_feed_element_events` vocabulary on the
 * frontend — the wire contract behind POST /api/stories/{id}/events.
 *
 * The backend owns this taxonomy in `backend/app/queries/user_event_types.py`
 * (`STATUS_EVENT_PAIRS`, `INTERACTION_EVENT_TYPES`, `VALID_EVENT_TYPES`);
 * this module is the frontend half of the same contract, pinned to the
 * backend owner by `backend/tests/test_mirror_parity.py`. The strings used to
 * be re-typed as bare literals across eight files — `recordEvent`'s parameter
 * was a plain `string`, so a typo compiled fine and the server silently
 * rejected it — MyActivityPage carried a third full copy of the vocabulary as
 * its label map, and each toggle site re-derived by hand which event flips
 * which story flag (read/unread -> is_read, ...). Route every event spelling
 * through this module so the vocabulary lives in exactly one place per stack,
 * with the parity test tying the two stacks together.
 */

// Status events come in [positive, negative] pairs and represent toggleable
// state; the key is the category, the first member means "this status is on".
export const STATUS_EVENT_PAIRS = {
  read: ['read', 'unread'],
} as const;

// Fire-and-forget analytics signals with no inverse and no status semantics.
export const INTERACTION_EVENT_TYPES = [
  'click_external',
  'expand',
  'show_summary',
  'expand_topic',
  'return_to_feed',
] as const;

export type StatusCategory = keyof typeof STATUS_EVENT_PAIRS;
export type StatusEventType = (typeof STATUS_EVENT_PAIRS)[StatusCategory][number];
export type InteractionEventType = (typeof INTERACTION_EVENT_TYPES)[number];
export type UserEventType = StatusEventType | InteractionEventType;
